import * as React from "react";
import { Button, ButtonProps } from "./button";
import { usePermissions } from "@/hooks/usePermissions";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";

export interface PermissionButtonProps extends ButtonProps {
  /** Single permission required to enable the button */
  permission?: string;
  /** Multiple permissions - user must have ANY of these */
  anyOf?: string[];
  /** Multiple permissions - user must have ALL of these */
  allOf?: string[];
  /**
   * Behavior when user lacks permission:
   * - "hide" (default): Button is not rendered
   * - "disable": Button is rendered but disabled with tooltip
   */
  whenDenied?: "hide" | "disable";
  /** Custom message shown in tooltip when disabled due to lack of permission */
  deniedMessage?: string;
}

/**
 * A Button that respects user permissions.
 *
 * @example
 * // Hide button if user can't create clients
 * <PermissionButton permission="clients:create" onClick={handleCreate}>
 *   Create Client
 * </PermissionButton>
 *
 * @example
 * // Disable with tooltip if user can't delete
 * <PermissionButton
 *   permission="clients:delete"
 *   whenDenied="disable"
 *   deniedMessage="You need delete permission"
 *   variant="destructive"
 * >
 *   Delete
 * </PermissionButton>
 *
 * @example
 * // Require any of multiple permissions
 * <PermissionButton anyOf={["clients:edit", "clients:delete"]}>
 *   Manage Client
 * </PermissionButton>
 */
export const PermissionButton = React.forwardRef<
  HTMLButtonElement,
  PermissionButtonProps
>(
  (
    {
      permission,
      anyOf,
      allOf,
      whenDenied = "hide",
      deniedMessage = "You don't have permission to perform this action",
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const { can, canAny, canAll, isLoading } = usePermissions();

    // Determine if user has required permission
    let hasAccess = true;
    if (permission) {
      hasAccess = can(permission);
    } else if (anyOf && anyOf.length > 0) {
      hasAccess = canAny(anyOf);
    } else if (allOf && allOf.length > 0) {
      hasAccess = canAll(allOf);
    }

    // While loading, show disabled button
    if (isLoading) {
      return (
        <Button ref={ref} disabled {...props}>
          {children}
        </Button>
      );
    }

    // If no permission and behavior is "hide", don't render
    if (!hasAccess && whenDenied === "hide") {
      return null;
    }

    // If no permission and behavior is "disable", show disabled button with tooltip
    if (!hasAccess && whenDenied === "disable") {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="inline-block">
                <Button ref={ref} disabled {...props}>
                  {children}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{deniedMessage}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    // User has permission, render normal button
    return (
      <Button ref={ref} disabled={disabled} {...props}>
        {children}
      </Button>
    );
  }
);

PermissionButton.displayName = "PermissionButton";
